import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router'

import { Err, Ok, Result } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { ConnectionSetupRoutes } from 'app/application/types'
import { Form } from 'app/common/presentation'
import { useConnections } from 'app/connections/application'
import { ConnectionConfig, ConnectionName } from 'app/connections/types'
import { mapToConnection } from 'app/connections/types/connections'

import { Button } from 'components'

import Authorization from '../Authorization'
import { useConnection } from '../Connection'
import { AuthorizationData } from '../Settings'
import styles from '../Setup.module.scss'

type SignInParams = {
  name: string
}

// config values some connections need before the authorization can be started
const preAuthorizationConfigKeys: Partial<Record<ConnectionName, string[]>> = {}

const SignIn = () => {
  const navigate = useNavigate()
  const { createNotification, te } = useNotifications()
  const { name } = useParams<SignInParams>() as SignInParams
  const { t } = useTranslation('connections')

  const connection = useConnection()
  const { saveConnection } = useConnections()

  const [preAuthorizationConfig, setPreAuthorizationConfig] = useState<Record<string, string>>({})

  if (!connection) {
    return <Navigate to={`/connections/${name}`} replace />
  }

  const preAuthorizationKeys = preAuthorizationConfigKeys[connection.name] ?? []
  const isPreAuthorizationComplete = preAuthorizationKeys.every(
    (key) => !!preAuthorizationConfig[key],
  )

  const handleAuthorizationData = async (
    authorizationData?: AuthorizationData,
  ): Promise<Result<void, string | void>> =>
    authorizationData
      ? saveConnection(
          connection,
          { ...connection.config, ...preAuthorizationConfig } as ConnectionConfig,
          authorizationData?.authorizationCode,
          authorizationData?.redirectUri,
        ).then((result) => {
          if (result.ok) {
            if (connection.name === 'amazon') {
              void navigate(`../${ConnectionSetupRoutes.ConnectionVerified}`, {
                state: { accounts: [], connection: mapToConnection(result.val, connection) },
              })
            } else {
              void navigate(`../${ConnectionSetupRoutes.SelectWorkspaces}`, {
                state: { connection: mapToConnection(result.val, connection) },
              })
            }

            return Ok(undefined)
          } else {
            createNotification({
              message: result.val.message,
              title: te(`http.${result.val.code}`),
              type: 'error',
            })

            return Err(result.val.message)
          }
        })
      : Promise.resolve(Ok(undefined))

  return (
    <>
      <div className={styles.form}>
        {preAuthorizationKeys.map((key) => (
          <Form.InputFormRow
            key={key}
            description={t(`setup.${connection.name}.config.${key}.description`)}
            mandatory
            onChange={(value) => setPreAuthorizationConfig((prev) => ({ ...prev, [key]: value }))}
            placeholder={t(`setup.${connection.name}.config.${key}.title`)}
            title={t(`setup.${connection.name}.config.${key}.title`)}
            value={preAuthorizationConfig[key] ?? ''}
          />
        ))}
        {connection.authConfig && isPreAuthorizationComplete && (
          <Authorization
            authorizationState={'PENDING'}
            redirectUri={`${window.location.origin}/connections/${name.toLowerCase()}`}
            setAuthorizationData={handleAuthorizationData}
            connection={connection}
          />
        )}
      </div>
      <div className={styles.actions}>
        <Button onClick={() => void navigate(-1)} variant="secondary">
          {t('setup.actions.back')}
        </Button>
      </div>
    </>
  )
}

export default SignIn
